import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { usePageMeta } from "../../components/Layout";

// D5 minimal Platform Users administration.
//
// Scope hard-lock (Strategy Feedback §2.3):
//   - view + edit first_name / last_name on existing admin users
//   - email is the account/auth identity — READ ONLY here
//   - NO Add User, NO delete, NO password admin, NO roles/teams,
//     NO invitations
//
// The default Project Owner is a read-only display; production
// operators reconfigure it via the API or a follow-up brief.

function RowEdit({ user, onSaved }) {
    const [first, setFirst] = useState(user.first_name || "");
    const [last, setLast] = useState(user.last_name || "");
    const [saving, setSaving] = useState(false);

    const dirty =
        (first || "") !== (user.first_name || "") ||
        (last  || "") !== (user.last_name  || "");

    const save = async () => {
        setSaving(true);
        try {
            const r = await api.patch(`/admin/users/${user.id}`, {
                first_name: first.trim() || null,
                last_name:  last.trim()  || null,
            });
            onSaved(r.data);
            toast.success("User updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    return (
        <tr data-testid={`users-row-${user.id}`} className="border-t border-[#E5E2DC]">
            <td className="py-3 px-4 text-[#666] font-mono text-[12px]" data-testid={`users-row-email-${user.id}`}>
                {user.email}
            </td>
            <td className="py-3 px-4">
                <input
                    type="text"
                    value={first}
                    onChange={(e) => setFirst(e.target.value)}
                    className="w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px] focus:outline-none focus:border-terracotta"
                    data-testid={`users-input-first-${user.id}`}
                    placeholder="First name"
                    maxLength={64}
                />
            </td>
            <td className="py-3 px-4">
                <input
                    type="text"
                    value={last}
                    onChange={(e) => setLast(e.target.value)}
                    className="w-full border border-[#E5E2DC] rounded px-2 py-1 text-[13px] focus:outline-none focus:border-terracotta"
                    data-testid={`users-input-last-${user.id}`}
                    placeholder="Last name"
                    maxLength={64}
                />
            </td>
            <td className="py-3 px-4 text-charcoal font-semibold" data-testid={`users-row-display-${user.id}`}>
                {user.display_name || <span className="text-[#999]">—</span>}
            </td>
            <td className="py-3 px-4">
                <button
                    type="button"
                    onClick={save}
                    disabled={!dirty || saving}
                    data-testid={`users-save-${user.id}`}
                    className="px-3 py-1 rounded bg-terracotta text-white text-[13px] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                    {saving ? "Saving…" : "Save"}
                </button>
            </td>
        </tr>
    );
}

export default function UsersPage() {
    usePageMeta({ title: "Users | Studio G4 Admin" });
    const [items, setItems] = useState([]);
    const [defaultOwnerId, setDefaultOwnerId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/users");
            setItems(r.data?.items || []);
            setDefaultOwnerId(r.data?.default_project_owner_id || null);
            setErr("");
        } catch (e) {
            setErr(e?.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const upsert = (updated) => {
        setItems((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
    };

    return (
        <div className="max-w-[1100px] mx-auto p-6" data-testid="users-page">
            <div className="mb-6">
                <h1 className="font-manrope font-bold text-charcoal text-[22px] mb-1">Platform Users</h1>
                <p className="text-[#666] text-[13px]">
                    Manage the platform users' display identity. Email remains the account/authentication identity.
                    Additional user administration (invites, roles, deletion) is not enabled in this release.
                </p>
            </div>

            {err && (
                <div className="mb-4 p-3 rounded bg-[#FDECEA] text-[#B71C1C] text-[13px]" data-testid="users-error">
                    {err}
                </div>
            )}

            {loading ? (
                <div className="text-[#666] text-[14px]">Loading users…</div>
            ) : (
                <div className="bg-white border border-[#E5E2DC] rounded-lg overflow-x-auto">
                    <table className="w-full text-[13px] text-left" data-testid="users-table">
                        <thead className="text-[#666] font-normal">
                            <tr>
                                <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Email (account identity)</th>
                                <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">First name</th>
                                <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Last name</th>
                                <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]">Display name</th>
                                <th className="font-medium py-3 px-4 border-b border-[#E5E2DC]" />
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="py-6 px-4 text-center text-[#999]">
                                        No platform users found.
                                    </td>
                                </tr>
                            )}
                            {items.map((u) => (
                                <RowEdit key={u.id} user={u} onSaved={upsert} />
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {defaultOwnerId && (
                <div className="mt-4 text-[12px] text-[#666]" data-testid="users-default-owner-hint">
                    Default Project Owner (auto-assigned to new enquiries):{" "}
                    <span className="font-semibold text-charcoal">
                        {items.find((u) => u.id === defaultOwnerId)?.display_name || defaultOwnerId}
                    </span>
                </div>
            )}
        </div>
    );
}
